import React, { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import MainLayout from '../../layouts/MainLayout';
import userStore from '../../store/userStore';
import api, { getErrorMessage } from '../../services/api';
import { STATUSES_WITH_ACTIVE_MANAGEMENT, expedientStatusLabel } from '@claimup/shared/constants/expedient';

/**
 * Reglas de salida de AR: qué pasa con los expedientes que no se cobran.
 * Una sola salida automática (a recobro o de baja), condiciones con O y estados
 * que suspenden la regla. La vista previa dice qué saldría hoy antes de
 * activarla. Ver docs/ar-reglas-salida-plan.md.
 */

export const DEACTIVATION_STATES = ['Fallido', 'Ilocalizable', 'Sin datos de contacto', 'Cancelled'];
const CHANNELS = [
  ['whatsapp', 'WhatsApps'],
  ['email', 'emails'],
  ['sms', 'SMS'],
  ['call', 'llamadas'],
];

const eur = n =>
  new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 2 }).format(n || 0);

/** Lo que se manda al servidor: los campos vacíos van como `null` (= no se usa). */
export const toPayload = form => ({
  enabled: form.enabled,
  action: form.action,
  deactivate_state: form.deactivate_state,
  max_days_in_ar: form.max_days_in_ar === '' ? null : Number(form.max_days_in_ar),
  unanswered_attempts: Object.fromEntries(
    CHANNELS.map(([c]) => [c, form.unanswered_attempts[c] === '' ? null : Number(form.unanswered_attempts[c])])
  ),
  excluded_states: form.excluded_states,
});

const fromRule = rule => ({
  enabled: Boolean(rule.enabled),
  action: rule.action || 'escalate',
  deactivate_state: rule.deactivate_state || 'Fallido',
  max_days_in_ar: rule.max_days_in_ar ?? '',
  unanswered_attempts: Object.fromEntries(CHANNELS.map(([c]) => [c, rule.unanswered_attempts?.[c] ?? ''])),
  excluded_states: rule.excluded_states || [],
});

function ArExitRulesPage() {
  const { user } = userStore();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);

  useEffect(() => {
    if (user?.role !== 'company') return;
    api
      .get('/ar-exit/rules')
      .then(({ data }) => setForm(fromRule(data.data)))
      .catch(err => toast.error(getErrorMessage(err)));
  }, [user?.role]);

  if (user?.role !== 'company') return <Navigate to='/dashboard' replace />;

  const set = (key, value) => setForm(f => ({ ...f, [key]: value }));
  const setAttempt = (c, value) => setForm(f => ({ ...f, unanswered_attempts: { ...f.unanswered_attempts, [c]: value } }));
  const toggleExcluded = estado =>
    setForm(f => ({
      ...f,
      excluded_states: f.excluded_states.includes(estado)
        ? f.excluded_states.filter(e => e !== estado)
        : [...f.excluded_states, estado],
    }));

  const guardar = async () => {
    setSaving(true);
    try {
      const { data } = await api.put('/ar-exit/rules', toPayload(form));
      setForm(fromRule(data.data));
      toast.success(data.data.enabled ? 'Regla guardada y activada' : 'Regla guardada (desactivada)');
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const verQueSaldria = async () => {
    setPreviewing(true);
    try {
      const { data } = await api.post('/ar-exit/rules/preview', { rule: toPayload(form) });
      setPreview(data.data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setPreviewing(false);
    }
  };

  const salida = form?.action === 'deactivate' ? `de baja (${form.deactivate_state})` : 'a recobro';

  return (
    <MainLayout>
      <div className='space-y-4 max-w-4xl'>
        <header className='bg-white border border-slate-200 rounded-lg px-5 py-4'>
          <h1 className='text-2xl font-semibold text-slate-900'>Reglas de salida de AR</h1>
          <p className='text-sm text-slate-500 mt-1'>
            Qué pasa con los expedientes que no se cobran. Con la regla activada, cada mañana salen de AR los que la
            cumplen. Sin ella, se hace a mano desde cada expediente.
          </p>
        </header>

        {!form && <p className='text-sm text-slate-500'>Cargando…</p>}

        {form && (
          <>
            <section className='bg-white border border-slate-200 rounded-lg px-5 py-4 space-y-5'>
              <label className='flex items-center gap-3 text-sm font-medium text-slate-800'>
                <input type='checkbox' checked={form.enabled} onChange={e => set('enabled', e.target.checked)} />
                Activar la salida automática
              </label>

              <fieldset>
                <legend className='text-sm font-semibold text-slate-800 mb-2'>¿Qué hacemos con ellos?</legend>
                <div className='space-y-2 text-sm text-slate-700'>
                  <label className='flex items-center gap-2'>
                    <input
                      type='radio'
                      name='action'
                      checked={form.action === 'escalate'}
                      onChange={() => set('action', 'escalate')}
                    />
                    Pasarlos a recobro: los gestiona ClaimUp
                  </label>
                  <label className='flex items-center gap-2 flex-wrap'>
                    <input
                      type='radio'
                      name='action'
                      checked={form.action === 'deactivate'}
                      onChange={() => set('action', 'deactivate')}
                    />
                    Darlos de baja, como
                    <select
                      aria-label='Estado de baja'
                      value={form.deactivate_state}
                      onChange={e => set('deactivate_state', e.target.value)}
                      disabled={form.action !== 'deactivate'}
                      className='rounded-lg border-slate-300 text-sm'
                    >
                      {DEACTIVATION_STATES.map(s => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
              </fieldset>

              <fieldset>
                <legend className='text-sm font-semibold text-slate-800 mb-1'>Cuándo</legend>
                <p className='text-xs text-slate-500 mb-3'>
                  Basta con que se cumpla una. Lo que dejes vacío no cuenta. Los intentos se cuentan desde la última vez
                  que el deudor respondió.
                </p>
                <div className='grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm text-slate-700'>
                  <label className='flex items-center gap-2'>
                    Al cabo de
                    <input
                      type='number'
                      min='1'
                      aria-label='Días en AR'
                      value={form.max_days_in_ar}
                      onChange={e => set('max_days_in_ar', e.target.value)}
                      className='w-20 rounded-lg border-slate-300 text-sm'
                    />
                    días en AR
                  </label>
                  {CHANNELS.map(([c, nombre]) => (
                    <label key={c} className='flex items-center gap-2'>
                      Con
                      <input
                        type='number'
                        min='1'
                        aria-label={`Intentos por ${nombre}`}
                        value={form.unanswered_attempts[c]}
                        onChange={e => setAttempt(c, e.target.value)}
                        className='w-20 rounded-lg border-slate-300 text-sm'
                      />
                      {nombre} sin respuesta
                    </label>
                  ))}
                </div>
              </fieldset>

              <fieldset>
                <legend className='text-sm font-semibold text-slate-800 mb-1'>Nunca mientras esté en</legend>
                <div className='flex flex-wrap gap-3 text-sm text-slate-700'>
                  {STATUSES_WITH_ACTIVE_MANAGEMENT.map(estado => (
                    <label key={estado} className='flex items-center gap-1.5'>
                      <input
                        type='checkbox'
                        checked={form.excluded_states.includes(estado)}
                        onChange={() => toggleExcluded(estado)}
                      />
                      {expedientStatusLabel(estado)}
                    </label>
                  ))}
                </div>
              </fieldset>

              <div className='flex flex-wrap gap-2 pt-2'>
                <button
                  type='button'
                  onClick={verQueSaldria}
                  disabled={previewing}
                  className='px-4 py-2 rounded-lg border border-slate-300 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-60'
                >
                  {previewing ? 'Calculando…' : '¿Qué saldría hoy?'}
                </button>
                <button
                  type='button'
                  onClick={guardar}
                  disabled={saving}
                  className='px-4 py-2 rounded-lg text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60'
                >
                  {saving ? 'Guardando…' : 'Guardar'}
                </button>
              </div>
            </section>

            {preview && (
              <section className='bg-white border border-slate-200 rounded-lg overflow-x-auto' aria-label='Vista previa'>
                <h2 className='px-5 pt-4 font-semibold text-slate-800'>
                  {preview.count === 0
                    ? 'Hoy no saldría ningún expediente'
                    : `Hoy saldrían ${preview.count} expedientes ${salida}`}
                </h2>
                {preview.count > 0 && (
                  <table className='min-w-full text-sm mt-2'>
                    <tbody className='divide-y divide-slate-100'>
                      {preview.exits.map(e => (
                        <tr key={e.expedient_id}>
                          <td className='px-4 py-2'>
                            <Link to={`/view/${e.expedient_id}`} className='text-indigo-600 hover:text-indigo-800'>
                              {e.debtorname || 'Expediente'}
                            </Link>
                          </td>
                          <td className='px-4 py-2 text-slate-600'>{e.estado}</td>
                          <td className='px-4 py-2 text-right tabular-nums'>{eur(e.importe_deuda)}</td>
                          <td className='px-4 py-2 text-slate-500'>{e.reason.replace('Regla automática: ', '')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </section>
            )}
          </>
        )}
      </div>
    </MainLayout>
  );
}

export default ArExitRulesPage;
